/**
 * The one page bar a settings screen gets — `design-remediation.md` §1.1.
 *
 * Every page under `/settings` used to draw its own `<PageBar>` on top of the
 * shell's, which is why the as-built screenshots show two stacked bars: one
 * reading "Settings / Users & roles" and, directly under it, "Keystone Grounds
 * / People". A bar is the answer to "where am I", and two of them is two
 * answers.
 *
 * So the shell owns the bar and a page *describes* it. The context lives in
 * its own module rather than in `settings-shell.tsx` because the shell imports
 * every page and every page needs the hook: sharing the file would be a cycle.
 *
 * The words go through state; the **buttons go through a portal**. That split
 * is the whole design. A Save button is a fresh React element on every render
 * of the page, so pushing it into the shell's state would set state from an
 * effect on every render — a loop. The shell instead leaves an empty span in
 * the bar and the page renders its buttons into it, so a button that turns
 * from disabled to enabled updates without the shell re-rendering at all.
 *
 * A page mounted outside the shell — the package builder is reachable on its
 * own, My account is not a company setting — finds no context and the hook
 * does nothing. That is on purpose: the hook is a request, not a requirement.
 */
import {
  createContext, useContext, useEffect, useMemo, useState, type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'

/**
 * One crumb after "Settings".
 *
 * A plain string is a crumb that names a settings page and is looked up by
 * label. `{ label, to }` is a crumb that knows where it goes — for a tab,
 * which is not a page and so cannot be found by name. It used to be that
 * such a crumb was drawn inert on purpose, which made "Packages" in the
 * package builder a word that looks like a link and does nothing.
 */
export type Crumb = string | { label: string; to: string }

export interface SettingsBarInfo {
  title: string
  /** The 12px faint line after the title. */
  sub?: string
  /** Crumbs *after* "Settings", which the shell always draws first. */
  crumbs?: Crumb[]
  /** Rendered into the bar's right-hand slot through a portal. */
  actions?: ReactNode
  /** Chips after the title — the package's `Offered` and `Package` (cb-05). */
  tags?: BarTag[]
}

export interface BarTag { label: string; tone: 'ok' | 'plain' }

interface SettingsBarApi {
  set(bar: { title: string; sub?: string; crumbs?: Crumb[]; tags?: BarTag[] } | null): void
  /** Where a page's buttons land. Null until the bar has mounted. */
  host: HTMLElement | null
}

const SettingsBarContext = createContext<SettingsBarApi | null>(null)

/**
 * The shell's half: the words the bar should say, and the slot to draw.
 *
 * `provide` wraps whatever the shell renders under the bar, and `slot` is what
 * it hands `<PageBar actions={…}>`.
 */
export function useSettingsBarState(): {
  bar: { title: string; sub?: string; crumbs?: Crumb[]; tags?: BarTag[] } | null
  slot: ReactNode
  provide: (children: ReactNode) => ReactNode
} {
  const [bar, setBar] = useState<{ title: string; sub?: string; crumbs?: Crumb[]; tags?: BarTag[] } | null>(null)
  const [host, setHost] = useState<HTMLElement | null>(null)
  const api = useMemo<SettingsBarApi>(() => ({ set: setBar, host }), [host])

  return {
    bar,
    /*
     * `display: contents` so the buttons the page portals in become flex
     * items of the bar's own action row, rather than one crowded child of it.
     */
    slot: <span ref={setHost} className="contents" />,
    provide: (children) => (
      <SettingsBarContext.Provider value={api}>{children}</SettingsBarContext.Provider>
    ),
  }
}

/**
 * What this page wants the bar to say.
 *
 * Returns the node that carries its buttons. Render it anywhere — it is a
 * portal, so where it sits in the page's own tree does not matter — and drop
 * it if the page has no actions.
 */
export function useSettingsBar(info: SettingsBarInfo | null): ReactNode {
  const api = useContext(SettingsBarContext)

  const title = info?.title ?? ''
  const sub = info?.sub
  // A string, so an array literal rebuilt on every render does not re-fire.
  const crumbs = JSON.stringify(info?.crumbs ?? [])
  const tags = JSON.stringify(info?.tags ?? [])

  useEffect(() => {
    if (!api) return
    if (!title) {
      api.set(null)
      return
    }
    const parsed = JSON.parse(crumbs) as Crumb[]
    const chips = JSON.parse(tags) as BarTag[]
    api.set({
      title, sub, crumbs: parsed.length > 0 ? parsed : undefined,
      tags: chips.length > 0 ? chips : undefined,
    })
    return () => api.set(null)
  }, [api, title, sub, crumbs, tags])

  if (!api?.host || !info?.actions) return null
  return createPortal(info.actions, api.host)
}
